import FolderClient from '../../client/folder/FolderClient';
import ComponentName from '../../constant/ComponentName';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import Modal from './view/Modal';
import { cloneDeep } from 'lodash';
import { ItemType } from './view/Item';
import ItemBuilderView from './view/item/ItemBuilderView';
import RoleHelper from '../../RoleHelper';
import ComponentCategoryUtil from '../../util/ComponenyCategoryUtil';

class DraggableItemService {
  private readonly folderClient: FolderClient;

  constructor(folderClient: FolderClient) {
    this.folderClient = folderClient;
  }

  private insertCurrentFolderToTargetFolder(
    component: ComponentName,
    current: Modal | undefined,
    target: Modal | undefined,
    currentCategory: EnrichedComponentCategory | undefined,
  ) {
    if (!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))) return;
    if (current && target && currentCategory && current.id != target.id && currentCategory.id != current.id) {
      try {
        this.insertCategoryToTargetCategory(component, current, target);
      } catch (e) {
        console.error(e);
      }
    }
  }

  private insertItemToTargetFolder(
    component: ComponentName,
    current: Modal | undefined,
    target: Modal | undefined,
    currentCategory: EnrichedComponentCategory | undefined,
  ) {
    if (!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))) return;

    if (current && target && currentCategory) {
      try {
        this.insertItemToTargetCategory(component, current, target);
      } catch (e) {
        console.error(e);
      }
    }
  }

  private insertMultipleItemsToTargetFolder(
    component: ComponentName,
    items: any[],
    target: Modal | undefined,
    currentCategory: EnrichedComponentCategory | undefined,
  ) {
    if (!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))) return;
    if (target && currentCategory && items.find((item) => item.id != target.id)) {
      const category = cloneDeep(target.value?.payload);
      if (category) {
        items.forEach((item: any) => {
          this.folderClient.addItemToCategory(component, target.value?.payload.id, item.name);
        });
      }
    }
  }

  private insertMultipleFoldersToTargetFolder(
    component: ComponentName,
    folders: Modal[],
    target: Modal | undefined,
    currentCategory: EnrichedComponentCategory | any | undefined,
  ) {
    if (!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))) return;

    if (currentCategory && target && folders.find((folder) => folder.value?.payload.id != target.value?.payload.id)) {
      const currentFolder = cloneDeep(currentCategory);
      const targetFolder = cloneDeep(target?.value?.payload);
      delete currentFolder.path;
      delete targetFolder.path;

      folders.forEach((folder: Modal) => {
        delete folder.value?.payload.path;
        this.folderClient.addCategoryToCategory(component, targetFolder.id, folder.value?.payload);
      });
    }
  }

  private insertItemToTargetCategory(component: ComponentName, current: Modal, target: Modal) {
    this.folderClient.addItemToCategory(component, target.value?.payload.id, current.value?.payload.name);
  }

  private insertCategoryToTargetCategory(component: ComponentName, current: Modal, target: Modal) {
    const currentFolder: EnrichedComponentCategory | any = cloneDeep(current.value?.payload);
    delete currentFolder.path;
    this.folderClient.addCategoryToCategory(component, target.value?.payload.id, currentFolder);
  }

  private checkIsFolderToFolder(selectedFolders: Modal[], current: Modal, target: Modal, currentCategory: EnrichedComponentCategory) {
    return (
      selectedFolders &&
      selectedFolders.length <= 0 &&
      current &&
      target &&
      current.value?.payload.id != target.value?.payload.id &&
      target.value?.payload.id != currentCategory.id &&
      current.value?.type == ItemType.FOLDER &&
      (target.value?.type == ItemType.FOLDER || target.value?.type == ItemType.BREADCRUMB)
    );
  }

  private checkIsItemToFolder(selectedItems: Modal[], current: Modal, target: Modal, currentCategory: EnrichedComponentCategory) {
    return (
      selectedItems &&
      selectedItems.length <= 0 &&
      current != undefined &&
      target != undefined &&
      current.value?.type == ItemType.LIST &&
      (target.value?.type == ItemType.FOLDER || target.value?.type == ItemType.BREADCRUMB) &&
      currentCategory.id != target.value?.payload.id
    );
  }

  private checkIsMultipleItemToFolder(selectedItems: Modal[], current: Modal, target: Modal, currentCategory: EnrichedComponentCategory) {
    return (
      selectedItems &&
      selectedItems.length > 0 &&
      current != undefined &&
      target != undefined &&
      current.value?.type == ItemType.LIST &&
      current.value.payload.id != currentCategory.id &&
      (target.value?.type == ItemType.FOLDER || target.value?.type == ItemType.BREADCRUMB)
    );
  }

  private checkIsMultipleFolderToFolder(selectedFolders: Modal[], current: Modal, target: Modal, currentCategory: EnrichedComponentCategory) {
    return (
      selectedFolders &&
      selectedFolders.length > 0 &&
      current &&
      target &&
      current.value?.payload.id != target.value?.payload.id &&
      target.value?.payload.id != currentCategory.id &&
      current.value?.type == ItemType.FOLDER &&
      (target.value?.type == ItemType.FOLDER || target.value?.type == ItemType.BREADCRUMB)
    );
  }

  public viewsDidUpdate(
    itemBuilderView: ItemBuilderView,
    current: Modal,
    target: Modal,
    component: ComponentName,
    currentCategory: EnrichedComponentCategory,
    selectedFolders: Modal[],
    selectedItems: Modal[],
    setSelectedItems: (selectedItems: Modal[]) => void,
    setSelectedFolders: (selectedFolders: Modal[]) => void,
  ) {
    if (itemBuilderView != undefined && itemBuilderView?.getLastTarget() != undefined && itemBuilderView?.getLastCurrent() != undefined && currentCategory) {
      if (this.checkIsFolderToFolder(selectedFolders, itemBuilderView?.getLastCurrent()!, itemBuilderView?.getLastTarget()!, currentCategory)) {
        // folder to folder
        this.insertCurrentFolderToTargetFolder(component, itemBuilderView?.getLastCurrent()!, itemBuilderView?.getLastTarget()!, currentCategory);
      } else if (this.checkIsItemToFolder(selectedItems, itemBuilderView?.getLastCurrent()!, itemBuilderView?.getLastTarget()!, currentCategory)) {
        // list to folder
        this.insertItemToTargetFolder(component, itemBuilderView?.getLastCurrent()!, itemBuilderView?.getLastTarget()!, currentCategory);
      } else if (this.checkIsMultipleItemToFolder(selectedItems, itemBuilderView?.getLastCurrent()!, itemBuilderView.getLastTarget()!, currentCategory)) {
        // multiple list to folder
        this.insertMultipleItemsToTargetFolder(component, selectedItems, itemBuilderView?.getLastTarget(), currentCategory);
        setSelectedItems([]);
      } else if (this.checkIsMultipleFolderToFolder(selectedFolders, itemBuilderView?.getLastCurrent()!, itemBuilderView?.getLastTarget()!, currentCategory)) {
        // multiple folder to folder
        if (!selectedFolders.find((c: Modal) => c.value?.payload.id == itemBuilderView?.getLastTarget()?.value?.payload.id)) {
          this.insertMultipleFoldersToTargetFolder(component, selectedFolders, itemBuilderView?.getLastTarget()!, currentCategory);
          setSelectedFolders([]);
        }
      }
    }
  }
}

export default DraggableItemService;
